import React, { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  IonModal, IonContent, IonButton, IonIcon,
  IonText, IonRow, IonCol, IonGrid,
} from '@ionic/react';
import { call, callOutline } from 'ionicons/icons';
import type { CallInvite } from '@capgo/capacitor-twilio-voice';
import { useTranslation } from 'react-i18next';
import { translateDynamic } from '../i18n/dynamic';

interface IncomingCallState {
  invite: CallInvite;
  accepting: boolean;
}

const IncomingCallOverlay: React.FC = () => {
  const { t } = useTranslation();
  const [incoming, setIncoming] = useState<IncomingCallState | null>(null);
  const listenersRef = useRef<Array<{ remove: () => Promise<void> }>>([]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let cancelled = false;
    (async () => {
      const { CapacitorTwilioVoice } = await import('@capgo/capacitor-twilio-voice');

      // CapacitorTwilioVoice handles the native notification UI, ringtone, vibration,
      // and Accept/Decline buttons natively. No IncomingCallKit needed on Android.
      const inviteHandle = await CapacitorTwilioVoice.addListener('callInviteReceived', (invite: any) => {
        if (cancelled) return;
        console.log('[IncomingCall] callInviteReceived sid=', invite.callSid);
      });

      const cancelHandle = await CapacitorTwilioVoice.addListener('callInviteCancelled', (data: any) => {
        if (cancelled) return;
        console.log('[IncomingCall] callInviteCancelled sid=', data.callSid);
      });

      listenersRef.current = [inviteHandle, cancelHandle];
    })();

    return () => {
      cancelled = true;
      listenersRef.current.forEach(h => h.remove());
      listenersRef.current = [];
    };
  }, []);

  useEffect(() => {
    // Web overlay only
    if (Capacitor.isNativePlatform()) return;

    let cancelled = false;

    (async () => {
      const { CapacitorTwilioVoice } = await import('@capgo/capacitor-twilio-voice');

      const inviteHandle = await CapacitorTwilioVoice.addListener('callInviteReceived', (invite) => {
        if (!cancelled) setIncoming({ invite, accepting: false });
      });

      const cancelHandle = await CapacitorTwilioVoice.addListener('callInviteCancelled', ({ callSid }) => {
        if (!cancelled) setIncoming(prev => prev?.invite.callSid === callSid ? null : prev);
      });

      const connectedHandle = await CapacitorTwilioVoice.addListener('callConnected', () => {
        if (!cancelled) setIncoming(null);
      });

      const disconnectedHandle = await CapacitorTwilioVoice.addListener('callDisconnected', ({ callSid }) => {
        if (!cancelled) setIncoming(prev => prev?.invite.callSid === callSid ? null : prev);
      });

      listenersRef.current = [inviteHandle, cancelHandle, connectedHandle, disconnectedHandle];

      try {
        const status = await CapacitorTwilioVoice.getCallStatus();
        if (!cancelled && status.pendingInvites.length > 0) {
          setIncoming(prev => prev ?? { invite: status.pendingInvites[0], accepting: false });
        }
      } catch { /* not fatal */ }
    })();

    return () => {
      cancelled = true;
      listenersRef.current.forEach(h => h.remove());
      listenersRef.current = [];
    };
  }, []);

  const handleAccept = async () => {
    if (!incoming) return;
    setIncoming(prev => prev ? { ...prev, accepting: true } : null);
    try {
      const { CapacitorTwilioVoice } = await import('@capgo/capacitor-twilio-voice');
      await CapacitorTwilioVoice.acceptCall({ callSid: incoming.invite.callSid });
    } catch (e) {
      console.error('[IncomingCall] acceptCall failed:', e);
      setIncoming(null);
    }
  };

  const handleReject = async () => {
    if (!incoming) return;
    try {
      const { CapacitorTwilioVoice } = await import('@capgo/capacitor-twilio-voice');
      await CapacitorTwilioVoice.rejectCall({ callSid: incoming.invite.callSid });
    } catch (e) {
      console.error('[IncomingCall] rejectCall failed:', e);
    }
    setIncoming(null);
  };

  // Native: kit handles ringing. Web only below.
  if (Capacitor.isNativePlatform()) return null;

  const callerDisplay = incoming?.invite.customParams?.callerName || incoming?.invite.from || translateDynamic('Unknown');

  return (
    <IonModal
      isOpen={!!incoming}
      backdropDismiss={false}
      style={{ '--height': '100%', '--width': '100%', '--border-radius': '0' }}
    >
      <IonContent style={{ '--background': 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)' } as React.CSSProperties}>
        <IonGrid>
          <IonRow className="ion-justify-content-center" style={{ paddingTop: '100px' }}>
            <IonCol size="12" className="ion-text-center">
              <IonText color="light">
                <p style={{ fontSize: '14px', opacity: 0.7, letterSpacing: '2px', textTransform: 'uppercase', margin: 0 }}>{t('components.IncomingCallOverlay.incomingCall')}
                </p>
              </IonText>
            </IonCol>
          </IonRow>

          <IonRow className="ion-justify-content-center ion-padding">
            <IonCol size="auto" className="ion-text-center">
              <IonText style={{ fontSize: '64px', lineHeight: 1 }}>📞</IonText>
            </IonCol>
          </IonRow>

          <IonRow className="ion-justify-content-center">
            <IonCol size="12" className="ion-text-center">
              <IonText color="light">
                <h2 style={{ fontSize: '28px', fontWeight: 600, margin: '0 24px' }}>{callerDisplay}</h2>
              </IonText>
            </IonCol>
          </IonRow>

          {incoming?.invite.from && incoming.invite.from !== callerDisplay && (
            <IonRow className="ion-justify-content-center">
              <IonCol size="12" className="ion-text-center">
                <IonText color="medium">
                  <p style={{ fontSize: '16px' }}>{incoming.invite.from}</p>
                </IonText>
              </IonCol>
            </IonRow>
          )}

          <IonRow className="ion-justify-content-center ion-align-items-start" style={{ marginTop: '64px', gap: '48px' }}>
            <IonCol size="auto" className="ion-text-center">
              <IonButton
                shape="round"
                color="danger"
                style={{ '--border-radius': '50%', width: '72px', height: '72px' }}
                onClick={handleReject}
                disabled={incoming?.accepting}
              >
                <IonIcon icon={callOutline} style={{ transform: 'rotate(135deg)', fontSize: '28px' }} />
              </IonButton>
              <IonText color="medium">
                <p style={{ fontSize: '12px', opacity: 0.7, marginTop: '8px' }}>{t('components.IncomingCallOverlay.decline')}</p>
              </IonText>
            </IonCol>

            <IonCol size="auto" className="ion-text-center">
              <IonButton
                shape="round"
                color="success"
                style={{ '--border-radius': '50%', width: '72px', height: '72px' }}
                onClick={handleAccept}
                disabled={incoming?.accepting}
              >
                <IonIcon icon={call} style={{ fontSize: '28px' }} />
              </IonButton>
              <IonText color="medium">
                <p style={{ fontSize: '12px', opacity: 0.7, marginTop: '8px' }}>{t('components.IncomingCallOverlay.accept')}</p>
              </IonText>
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonContent>
    </IonModal>
  );
};

export default IncomingCallOverlay;
